/**
 * Seeding a Y.Doc's ProseMirror fragment from a persisted document snapshot.
 *
 * The durable store stays the encrypted `document.json` snapshot (option A of
 * the ProseMirror migration — see persist.ts). Yjs is the live transport layer:
 * a fresh Y.Doc is seeded from that snapshot when a peer is first into a room,
 * and thereafter peers exchange updates over WebRTC. See session.ts for how the
 * "seed only if the room is empty" decision is made.
 *
 * COLLAB_FIELD MUST match the `field` Tiptap's Collaboration extension binds to
 * (its default is "default") and the fragment name used here — otherwise the
 * editor binds to a different, empty fragment than the one we seed.
 */
import { Node as PMNode, Schema } from "@tiptap/pm/model";
import * as Y from "yjs";
import { prosemirrorToYXmlFragment } from "y-prosemirror";

export const COLLAB_FIELD = "default";

/** Marks Yjs transactions this module originates, so observers can ignore them. */
export const SEED_ORIGIN = Symbol("qda-collab-seed");

export function fragmentIsEmpty(ydoc: Y.Doc): boolean {
  return ydoc.getXmlFragment(COLLAB_FIELD).length === 0;
}

/**
 * A Yjs client id derived deterministically from the content being seeded
 * (FNV-1a over the serialized snapshot). This is the crux of why seeding is
 * duplication-safe:
 *
 *  - Every peer that seeds the *same* snapshot builds its structs under the
 *    *same* client id, so the generated (client, clock) ids are identical and
 *    `Y.applyUpdate` treats a second peer's copy as already-known — an
 *    idempotent no-op merge instead of a second copy of the whole document.
 *    Without this, `prosemirrorToYDoc` minted a fresh RANDOM client id per
 *    seed, so two peers independently seeding the same doc (the reload / late-
 *    peer race in session.ts, where the 800ms sync wait loses to a slow WebRTC
 *    handshake) merged into DUPLICATED content.
 *  - Different content (e.g. a reseed to a newer snapshot — see clearFragment)
 *    hashes to a different client id, so its structs can't collide with the
 *    now-tombstoned clocks of the previous seed in the same Y.Doc.
 */
function seedClientId(contentJSON: unknown): number {
  const s = JSON.stringify(contentJSON);
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/**
 * Populate `ydoc`'s COLLAB_FIELD fragment from a ProseMirror-JSON document.
 * Builds the content in a throwaway Y.Doc under a content-derived client id
 * (see seedClientId) and merges it in as a single update — which, because the
 * ids are deterministic, reproduces the content exactly and, crucially, is
 * idempotent when another peer seeded the identical snapshot.
 */
export function seedYDocFromContent(ydoc: Y.Doc, contentJSON: unknown, schema: Schema): void {
  const pmDoc = PMNode.fromJSON(schema, contentJSON as Record<string, unknown>);
  const temp = new Y.Doc();
  // Set the deterministic client id before any structs are created on `temp`.
  temp.clientID = seedClientId(contentJSON);
  prosemirrorToYXmlFragment(pmDoc, temp.getXmlFragment(COLLAB_FIELD));
  const update = Y.encodeStateAsUpdate(temp);
  Y.applyUpdate(ydoc, update, SEED_ORIGIN);
  temp.destroy();
}

/** Empty the fragment (used before a reseed when no live peers are present). */
export function clearFragment(ydoc: Y.Doc): void {
  const frag = ydoc.getXmlFragment(COLLAB_FIELD);
  if (frag.length > 0) {
    ydoc.transact(() => frag.delete(0, frag.length), SEED_ORIGIN);
  }
}

/*
 * ---- Durable Yjs state (QdaDocument.ystate) ----
 *
 * Deterministic seeding only dedupes seeds of BYTE-IDENTICAL snapshots; once a
 * document has been edited, two peers' independently-built Y.Docs can never
 * merge cleanly. The durable fix is to persist the Yjs state itself (struct
 * ids and all) in document.json and RESTORE it on open instead of re-seeding:
 * every reload — and every device, since document.json round-trips through
 * cloud sync as one atom — then reconstructs the same shared lineage, and
 * merging with a peer's state is idempotent no matter how late the WebRTC
 * connection comes up. Seeding remains only for docs that predate ystate.
 */

/** Serialize a Y.Doc's full state for storage in QdaDocument.ystate. */
export function encodeDocState(ydoc: Y.Doc): string {
  const bytes = Y.encodeStateAsUpdate(ydoc);
  let binary = "";
  const chunkSize = 8192;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    binary += String.fromCharCode.apply(null, Array.from(chunk) as any);
  }
  return btoa(binary);
}

/** Decode a stored ystate back to a Yjs update. Null if corrupt. */
export function decodeDocState(base64: string): Uint8Array | null {
  try {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return bytes;
  } catch {
    return null;
  }
}

/**
 * Restore a (fresh, empty) Y.Doc from a persisted ystate. Returns false when
 * the state is corrupt or decodes to an empty document, so the caller falls
 * back to the legacy seed-from-content path instead of mounting an empty editor.
 */
export function applyEncodedDocState(ydoc: Y.Doc, base64: string): boolean {
  const update = decodeDocState(base64);
  if (!update) return false;
  try {
    Y.applyUpdate(ydoc, update, SEED_ORIGIN);
  } catch (err) {
    console.warn("[collab] failed to apply persisted Yjs state:", err);
    return false;
  }
  return !fragmentIsEmpty(ydoc);
}

/**
 * Whether `update` shares Yjs history with `ydoc` — i.e. at least one client id
 * appears in both. Shared lineage means Y.applyUpdate is a true CRDT merge
 * (identical structs dedupe, divergent edits interleave). Disjoint lineage
 * (two independent seeds) would CONCATENATE the documents, so callers must
 * replace (clearFragment + seed) instead of merging in that case.
 */
export function updateSharesLineage(ydoc: Y.Doc, update: Uint8Array): boolean {
  try {
    const local = Y.decodeStateVector(Y.encodeStateVector(ydoc));
    const meta = Y.parseUpdateMeta(update);
    for (const client of Array.from(meta.from.keys())) {
      if ((local.get(client) ?? 0) > 0) return true;
    }
    return false;
  } catch {
    return false;
  }
}
